import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Link, useLocation } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { useAuth } from "@/features/auth/state/useAuth";
import { extractApiError } from "@/lib/api-error";

const REMEMBERED_EMAIL_KEY = "ridetofive-admin-remembered-email";

const schema = z.object({
    email: z.string().email("Enter a valid work email"),
    password: z.string().min(6, "Password must be at least 6 characters"),
    remember: z.boolean().default(false)
});

type FormValues = z.infer<typeof schema>;

export const LoginForm = () => {
    const { login, isLoading } = useAuth();
    const location = useLocation();
    const routeMessage = (location.state as { message?: string } | null)?.message;
    const [showPassword, setShowPassword] = useState(false);
    const {
        register,
        handleSubmit,
        setError,
        setValue,
        formState: { errors }
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            email: "",
            password: "",
            remember: true
        }
    });

    // Load remembered email on mount
    useEffect(() => {
        const rememberedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY);
        if (rememberedEmail) {
            setValue("email", rememberedEmail);
        }
    }, [setValue]);

    const onSubmit = async ({ email, password, remember }: FormValues) => {
        try {
            await login(email, password);
            // Save email if "remember me" is checked
            if (remember) {
                localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
            } else {
                localStorage.removeItem(REMEMBERED_EMAIL_KEY);
            }
        } catch (error) {
            setError("root", { type: "server", message: extractApiError(error) });
        }
    };

    return (
        <div className="space-y-8">
            <header className="space-y-2">
                <p className="text-sm text-slate-500 dark:text-slate-300">
                    Sign in to keep trips, drivers, and payouts running smoothly.
                </p>
                {routeMessage ? <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400">{routeMessage}</p> : null}
            </header>

            <div className="border border-slate-200 bg-white/80 p-8 shadow-xl shadow-slate-900/5 backdrop-blur-md dark:border-slate-800 dark:bg-slate-900/80">
                <form className="space-y-6" onSubmit={handleSubmit(onSubmit)} noValidate>
                    <div className="space-y-1.5">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                            Admin email
                        </label>
                        <input
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100 dark:focus:border-primary-500"
                            placeholder="you@ridetofive.com"
                            {...register("email")}
                        />
                        {errors.email ? (
                            <p className="text-xs font-medium text-rose-500">{errors.email.message}</p>
                        ) : null}
                    </div>

                    <div className="space-y-1.5">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                            Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                autoComplete="current-password"
                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-12 text-sm text-slate-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100 dark:focus:border-primary-500"
                                placeholder="Enter your password"
                                {...register("password")}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? (
                                    <EyeSlashIcon className="h-5 w-5" />
                                ) : (
                                    <EyeIcon className="h-5 w-5" />
                                )}
                            </button>
                        </div>
                        {errors.password ? (
                            <p className="text-xs font-medium text-rose-500">{errors.password.message}</p>
                        ) : null}
                    </div>

                    <div className="flex items-center justify-between text-sm text-slate-500 dark:text-slate-300">
                        <label className="inline-flex items-center gap-2">
                            <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                                {...register("remember")}
                            />
                            Keep me signed in
                        </label>
                        <Link
                            to="/auth/forgot-password"
                            className="text-xs font-medium text-primary-600 transition hover:text-primary-500"
                        >
                            Forgot password?
                        </Link>
                    </div>

                    {errors.root ? (
                        <p className="text-sm font-medium text-rose-500">{errors.root.message}</p>
                    ) : null}

                    <Button type="submit" className="w-full rounded-full" disabled={isLoading}>
                        {isLoading ? "Signing in..." : "Access dashboard"}
                    </Button>

                    <Link to="/activate-account" className="block text-center text-sm font-medium text-primary-600 transition hover:text-primary-500">
                        Activate your admin account
                    </Link>

                </form>
            </div>

            <footer className="text-xs text-slate-400 dark:text-slate-500">
                Need help? Email{" "}
                <a
                    href="mailto:support@ridetofive.com"
                    className="font-medium text-primary-600 hover:text-primary-500"
                >
                    support@ridetofive.com
                </a>
                .
            </footer>
        </div>
    );
};
